import type { ComponentProps, HTMLAttributes, RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { Chart } from '@highcharts/react';
import { StockChart } from '@highcharts/react/Stock';

import { useChartContext } from '@/features/_shared/chart/context/ChartContext';
import { mergeChartEvents } from '@/features/_shared/chart/hooks/options/useChartFunctions';
import type { ChartRef, ExtendedOptions } from '@/features/_shared/chart/types/highcharts';
import { Highcharts } from '@/shared/lib/highcharts';

import type { HighchartsReactRefObject } from '@highcharts/react';
import type HighchartsType from 'highcharts';

type HighchartsReactChartProps = ComponentProps<typeof Chart>;

type ChartShellProps = {
  options: ExtendedOptions | undefined;
  isStockChart?: boolean;
  chartId?: string;
  containerProps?: HTMLAttributes<HTMLDivElement>;
  chartComponentRef?: RefObject<ChartRef | null>;
  onChartReady?: (chart: HighchartsType.Chart) => void;
};

const stripUpdateOnlyOptions = (options: ExtendedOptions): ExtendedOptions => {
  const { rangeSelector, ...rest } = options;
  return rangeSelector
    ? { ...rest, rangeSelector: { ...rangeSelector, selected: undefined } }
    : rest;
};

const ChartShell = ({
  options,
  isStockChart = false,
  chartId,
  containerProps,
  chartComponentRef,
  onChartReady
}: ChartShellProps) => {
  const internalRef = useRef<ChartRef | null>(null);
  const highchartsReactRef = useRef<HighchartsReactRefObject | null>(null);
  const lastReadyChartRef = useRef<HighchartsType.Chart | null>(null);
  const chartRefToUse = chartComponentRef ?? internalRef;
  const { chartRef } = useChartContext();

  const syncChartRef = useCallback(
    (chart: HighchartsType.Chart | undefined) => {
      if (!chart) return;
      const renderer = (chart as Partial<HighchartsType.Chart>).renderer;
      if (renderer?.forExport) return;

      chartRefToUse.current = { chart };
      chartRef.current = { chart };

      if (lastReadyChartRef.current !== chart) {
        lastReadyChartRef.current = chart;
        onChartReady?.(chart);
      }
    },
    [chartRef, chartRefToUse, onChartReady]
  );

  const handleChartReady = useCallback(
    function (this: HighchartsType.Chart) {
      syncChartRef(this);
    },
    [syncChartRef]
  );

  const optionsWithReadyHandler = useMemo(() => {
    if (!options) return options;
    return {
      ...options,
      chart: {
        ...options.chart,
        events: mergeChartEvents(options.chart?.events, { load: handleChartReady })
      }
    };
  }, [options, handleChartReady]);

  const chartOptions = useMemo(() => {
    if (!optionsWithReadyHandler || !chartRefToUse.current) return optionsWithReadyHandler;
    return stripUpdateOnlyOptions(optionsWithReadyHandler);
  }, [chartRefToUse, optionsWithReadyHandler]);

  useEffect(() => {
    syncChartRef(highchartsReactRef.current?.chart as HighchartsType.Chart | undefined);
  }, [chartOptions, syncChartRef]);

  useEffect(
    () => () => {
      chartRefToUse.current = null;
      chartRef.current = null;
      lastReadyChartRef.current = null;
    },
    [chartRef, chartRefToUse]
  );

  if (!chartOptions) return null;

  const ChartComponent = isStockChart ? StockChart : Chart;
  const highchartsInstance = Highcharts as unknown as HighchartsReactChartProps['highcharts'];
  const highchartsOptions = chartOptions as unknown as HighchartsReactChartProps['options'];

  return (
    <ChartComponent
      key={chartId}
      ref={highchartsReactRef}
      containerProps={containerProps}
      highcharts={highchartsInstance}
      options={highchartsOptions}
    />
  );
};

export default ChartShell;
